/**
 * Options Overlay tab — does conditioning each signal on options-market
 * metrics (ATM IV, skew) change its predictive power?
 */
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { AlphaVal, BENCH_LABELS, RunBar, SigBadge, StatCard, ToggleGroup } from "./common";

const GridCell = ({ cell }) => {
  if (!cell || cell.mean_pct === null || cell.n < 10)
    return <td className="text-center py-2 px-2 text-muted-foreground text-xs">-</td>;
  const v = cell.mean_pct;
  const bg = v > 0 ? `rgba(16,185,129,${Math.min(Math.abs(v) / 8, 0.5)})`
    : `rgba(239,68,68,${Math.min(Math.abs(v) / 8, 0.5)})`;
  return (
    <td className="text-center py-2 px-2" style={{ backgroundColor: bg }}>
      <div className="font-mono text-xs">{v > 0 ? "+" : ""}{v}%</div>
      <div className="text-[10px] text-muted-foreground">n={cell.n}</div>
    </td>
  );
};

const ConditioningCard = ({ comboKey, entry, bench }) => (
  <Card data-testid={`overlay-card-${comboKey}`}>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{entry.label}</CardTitle>
      <CardDescription className="text-xs">
        {entry.n_joined?.toLocaleString()} joined observations
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-5">
      {Object.entries(entry.option_metrics || {}).map(([om, omRes]) => {
        const r = omRes[bench];
        if (!r) return (
          <div key={om} className="text-xs text-muted-foreground">
            {omRes.label}: insufficient data for this benchmark
          </div>
        );
        return (
          <div key={om} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{omRes.label}</span>
              <SigBadge t={r.ic_diff_tstat} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <StatCard label="IC · low tercile">
                <AlphaVal value={r.ic_low_om} digits={4} suffix="" />
              </StatCard>
              <StatCard label="IC · high tercile">
                <AlphaVal value={r.ic_high_om} digits={4} suffix="" />
              </StatCard>
              <StatCard label="IC diff (t-stat)">
                <span className="font-mono text-sm">
                  {r.ic_diff ?? "-"} ({r.ic_diff_tstat ?? "-"})
                </span>
              </StatCard>
            </div>
            {r.double_sort_grid && (
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Double sort — rows: signal tercile (low→high) · cols: {omRes.label} tercile (low→high) · 3M fwd alpha
                </div>
                <table className="w-full border rounded text-xs">
                  <thead>
                    <tr className="text-muted-foreground border-b">
                      <th className="py-1 px-2 text-left font-medium">Sig \ Opt</th>
                      <th className="py-1 px-2 font-medium">Low</th>
                      <th className="py-1 px-2 font-medium">Mid</th>
                      <th className="py-1 px-2 font-medium">High</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.double_sort_grid.map((row, si) => (
                      <tr key={si} className="border-b last:border-0">
                        <td className="py-1 px-2 text-muted-foreground">
                          {["Low", "Mid", "High"][si]}
                        </td>
                        {row.map((cell, oi) => <GridCell key={oi} cell={cell} />)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </CardContent>
  </Card>
);

const ClusterConditioningCard = ({ conditioning, bench }) => {
  const metricKeys = Object.keys(conditioning || {});
  const [metric, setMetric] = useState(null);
  if (!metricKeys.length) return null;
  const active = metric && metricKeys.includes(metric) ? metric : metricKeys[0];
  const block = conditioning[active];
  const benchBlock = block?.[bench];

  return (
    <Card data-testid="overlay-cluster-conditioning">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Regime Refinement — Options Within Each Nowcast Cluster</CardTitle>
        <CardDescription className="text-xs">
          Forward 3M alpha of low vs high option-metric tercile per regime. The{" "}
          <strong>Interaction</strong> column nets out the universe-wide tercile effect (the
          common volatility factor) — only a significant interaction means the options market
          carries <em>regime-specific</em> information.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ToggleGroup
          testId="regime-metric"
          options={metricKeys.map((k) => ({ value: k, label: conditioning[k].label }))}
          value={active}
          onChange={setMetric}
        />
        {!benchBlock ? (
          <div className="text-xs text-muted-foreground py-4">
            Insufficient data for this benchmark.
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground">
              Universe-wide high−low tercile effect:{" "}
              <AlphaVal value={benchBlock.universe_diff_pct} /> (netted out in Interaction)
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-1.5 px-2 font-medium">Regime</th>
                    <th className="text-right py-1.5 px-2 font-medium">Low tercile α</th>
                    <th className="text-right py-1.5 px-2 font-medium">High tercile α</th>
                    <th className="text-right py-1.5 px-2 font-medium">High − Low</th>
                    <th className="text-right py-1.5 px-2 font-medium">Interaction</th>
                    <th className="text-right py-1.5 px-2 font-medium">Int. t</th>
                    <th className="text-right py-1.5 px-2 font-medium">Sig</th>
                  </tr>
                </thead>
                <tbody>
                  {(benchBlock.rows || []).map((r) => (
                    <tr key={r.cluster} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-1.5 px-2">
                        <span className="font-medium">{r.cluster}</span>
                        <span className="text-muted-foreground ml-1.5">{r.name}</span>
                        <span className="text-muted-foreground/70 ml-1">[{r.quality}]</span>
                      </td>
                      <td className="text-right py-1.5 px-2">
                        <AlphaVal value={r.low_mean_pct} />
                        <span className="text-muted-foreground/70 ml-1">n={r.low_n}</span>
                      </td>
                      <td className="text-right py-1.5 px-2">
                        <AlphaVal value={r.high_mean_pct} />
                        <span className="text-muted-foreground/70 ml-1">n={r.high_n}</span>
                      </td>
                      <td className="text-right py-1.5 px-2"><AlphaVal value={r.diff_pct} /></td>
                      <td className="text-right py-1.5 px-2"><AlphaVal value={r.interaction_pct} /></td>
                      <td className="text-right py-1.5 px-2 font-mono">{r.interaction_t ?? "-"}</td>
                      <td className="text-right py-1.5 px-2"><SigBadge t={r.interaction_t} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const DirectionalCrossCheckCard = ({ crosscheck, bench }) => {
  const comboKeys = Object.keys(crosscheck || {});
  const [combo, setCombo] = useState(null);
  const [metric, setMetric] = useState(null);
  if (!comboKeys.length) return null;
  const activeCombo = combo && comboKeys.includes(combo) ? combo : comboKeys[0];
  const entry = crosscheck[activeCombo];
  const metricKeys = Object.keys(entry?.metrics || {});
  const activeMetric = metric && metricKeys.includes(metric) ? metric : metricKeys[0];
  const m = entry?.metrics?.[activeMetric];
  const r = m?.[bench];

  return (
    <Card data-testid="overlay-crosscheck">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Directional Cross-Check — Confirm vs Contradict</CardTitle>
        <CardDescription className="text-xs">
          Does the options market's directional read confirm our signal? Confirmation premium =
          forward alpha when both agree minus when they disagree (Fama-MacBeth per date, NW t).
          Metrics are sign-oriented so "options bullish" always means bullish.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          <ToggleGroup
            testId="xc-signal"
            options={comboKeys.map((k) => ({ value: k, label: crosscheck[k].label }))}
            value={activeCombo}
            onChange={setCombo}
          />
        </div>
        <ToggleGroup
          testId="xc-metric"
          options={metricKeys.map((k) => ({ value: k, label: entry.metrics[k].label }))}
          value={activeMetric}
          onChange={setMetric}
        />
        {m && <div className="text-xs text-muted-foreground italic">{m.rationale}</div>}
        {!r ? (
          <div className="text-xs text-muted-foreground py-4">
            Insufficient data for this benchmark / metric.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 2x2 grid */}
            <div>
              <div className="text-xs text-muted-foreground mb-1">
                Pooled 3M fwd alpha — rows: our signal · cols: options read
              </div>
              <table className="w-full border rounded text-xs">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="py-1 px-2 text-left font-medium">Ours \ Options</th>
                    <th className="py-1 px-2 font-medium">Bearish</th>
                    <th className="py-1 px-2 font-medium">Bullish</th>
                  </tr>
                </thead>
                <tbody>
                  {r.grid.map((row, si) => (
                    <tr key={si} className="border-b last:border-0">
                      <td className="py-1.5 px-2 text-muted-foreground">
                        {["Bullish", "Bearish"][si]}
                      </td>
                      {row.map((cell, oi) => {
                        const confirmed = (si === 0 && oi === 1) || (si === 1 && oi === 0);
                        return (
                          <td key={oi}
                            className={`text-center py-1.5 px-2 ${confirmed ? "bg-muted/50" : ""}`}>
                            <AlphaVal value={cell.mean_pct} />
                            <div className="text-[10px] text-muted-foreground">
                              n={cell.n}{confirmed ? " · agree" : ""}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Premiums */}
            <div className="space-y-2">
              {[["Long confirmation premium", r.long_premium,
                 "Among our bullish names: options-bullish minus options-bearish"],
                ["Short confirmation premium", r.short_premium,
                 "Among our bearish names: options-bullish minus options-bearish (positive = options-bearish confirmation makes weak names fall more)"]].map(
                ([label, p, desc]) => (
                  <div key={label} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">{label}</span>
                      <SigBadge t={p?.nw_tstat} />
                    </div>
                    <div className="text-xl font-bold">
                      <AlphaVal value={p?.mean_pct} />
                      <span className="text-xs font-normal text-muted-foreground ml-2">
                        t={p?.nw_tstat ?? "-"} · {p?.pct_positive ?? "-"}% dates + · n={p?.n_dates ?? "-"}
                      </span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{desc}</div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const EventFilterCard = ({ eventFilter, bench }) => {
  const per = eventFilter?.per_signal || {};
  const keys = Object.keys(per);
  if (!keys.length) return null;
  return (
    <Card data-testid="overlay-event-filter">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Earnings / Event Filter — Inverted IV Term Structure</CardTitle>
        <CardDescription className="text-xs">
          Names where near-month ATM IV exceeds ~75d IV by 2+ vol pts have an imminent event
          priced in. Does excluding them sharpen each signal's IC?
        </CardDescription>
      </CardHeader>
      <CardContent>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="text-left py-1.5 px-2 font-medium">Signal</th>
              <th className="text-right py-1.5 px-2 font-medium">Flagged</th>
              <th className="text-right py-1.5 px-2 font-medium">IC (all)</th>
              <th className="text-right py-1.5 px-2 font-medium">IC (ex-event)</th>
              <th className="text-right py-1.5 px-2 font-medium">Δ IC</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const e = per[k];
              const b = e[bench];
              if (!b) return null;
              const delta = b.ic_ex_event != null && b.ic_all != null
                ? +(b.ic_ex_event - b.ic_all).toFixed(4) : null;
              return (
                <tr key={k} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-1.5 px-2 font-medium">{e.label}</td>
                  <td className="text-right py-1.5 px-2 font-mono">{b.flagged_pct}%</td>
                  <td className="text-right py-1.5 px-2 font-mono">
                    {b.ic_all} <span className="text-muted-foreground">(t={b.t_all})</span>
                  </td>
                  <td className="text-right py-1.5 px-2 font-mono">
                    {b.ic_ex_event} <span className="text-muted-foreground">(t={b.t_ex_event})</span>
                  </td>
                  <td className="text-right py-1.5 px-2"><AlphaVal value={delta} digits={4} suffix="" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

export const OptionsOverlayTab = ({ signal, testIdPrefix = "overlay" }) => {
  const { result, job, running, run } = signal;
  const [bench, setBench] = useState("sector");
  const res = result?.result;

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-tab`}>
      <Card>
        <CardHeader>
          <CardTitle>Options Market Conditioning</CardTitle>
          <CardDescription>
            Historical options metrics (Massive.com, ~2yr window): ATM IV, IV spread, put skew,
            risk reversal, call/put volume flow, O/S ratio, variance risk premium and IV term
            structure — via Black-Scholes inversion of contract closes. Tests confirmation /
            contradiction of each signal, regime-specific interactions and event filtering.
            First run fetches option chains (~20-30 min); metrics are cached for instant re-runs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RunBar
            label="Options Overlay"
            result={result}
            job={job}
            running={running}
            onRun={run}
            testId={`${testIdPrefix}-runbar`}
          />
          {res && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Universe">{res.coverage?.universe_size} symbols</StatCard>
                <StatCard label="Symbol-dates">{res.coverage?.pairs_requested?.toLocaleString()}</StatCard>
                <StatCard label="With metrics">{res.coverage?.pairs_with_metrics?.toLocaleString()}</StatCard>
                <StatCard label="Window start">{res.coverage?.options_window_start}</StatCard>
              </div>
              <ToggleGroup
                testId={`${testIdPrefix}-bench`}
                options={[
                  { value: "sector", label: BENCH_LABELS.sector },
                  { value: "spy", label: BENCH_LABELS.spy },
                ]}
                value={bench}
                onChange={setBench}
              />
            </>
          )}
        </CardContent>
      </Card>

      {res && (
        <>
          <DirectionalCrossCheckCard crosscheck={res.directional_crosscheck} bench={bench} />
          <EventFilterCard eventFilter={res.event_filter} bench={bench} />
          <ClusterConditioningCard conditioning={res.cluster_conditioning} bench={bench} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {Object.entries(res.analysis || {}).map(([key, entry]) => (
              <ConditioningCard key={key} comboKey={key} entry={entry} bench={bench} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
